import type { TourDefinition } from './types';
import { creatingAccountSteps } from './steps/creating-account';
import { settingUpProfileSteps } from './steps/setting-up-profile';
import { addingLicenseSteps } from './steps/adding-license';
import { navigatingDashboardSteps } from './steps/navigating-dashboard';
import { usingCalendarSteps } from './steps/using-calendar';
import { managingInquiriesSteps } from './steps/managing-inquiries';
import { convertingLeadsSteps } from './steps/converting-leads';
import { mileageSetupSteps } from './steps/mileage-setup';
import { mileageReportSteps } from './steps/mileage-report';
import { creatingTeamSteps } from './steps/creating-team';
import { invitingMembersSteps } from './steps/inviting-members';
import { compDatabaseSteps } from './steps/comp-database';
import { traineeLoggingSteps } from './steps/trainee-logging';
import { supervisorReviewSteps } from './steps/supervisor-review';
import { billingSteps } from './steps/billing';
import { accountSecuritySteps } from './steps/account-security';
import { buildingWebsiteSteps } from './steps/building-website';
import { companyProfileSteps } from './steps/company-profile';
import { marketReportSteps } from './steps/market-report';
import { taxAppealSteps } from './steps/tax-appeal';
import { sketchToolSteps } from './steps/sketch-tool';
import { dcfAnalyzerSteps } from './steps/dcf-analyzer';
import { creatingAppraisalSteps } from './steps/creating-appraisal';
import { creatingEngagementSteps } from './steps/creating-engagement';
import { managingEngagementsSteps } from './steps/managing-engagements';
import { eoCheckSteps } from './steps/eo-check';

const definitions: TourDefinition[] = [
  {
    id: 'creating-account',
    startRoute: '/dashboard',
    steps: creatingAccountSteps,
  },
  {
    id: 'setting-up-profile',
    startRoute: '/business?tab=profile',
    steps: settingUpProfileSteps,
  },
  {
    id: 'adding-license',
    startRoute: '/business?tab=profile',
    steps: addingLicenseSteps,
  },
  {
    id: 'navigating-dashboard',
    startRoute: '/dashboard',
    steps: navigatingDashboardSteps,
  },
  {
    id: 'using-calendar',
    startRoute: '/calendar',
    steps: usingCalendarSteps,
  },
  {
    id: 'managing-inquiries',
    startRoute: '/leads',
    steps: managingInquiriesSteps,
  },
  {
    id: 'converting-leads',
    startRoute: '/leads',
    steps: convertingLeadsSteps,
  },
  {
    id: 'mileage-setup',
    startRoute: '/mileage',
    steps: mileageSetupSteps,
  },
  {
    id: 'mileage-report',
    startRoute: '/mileage',
    steps: mileageReportSteps,
  },
  {
    id: 'creating-team',
    startRoute: '/business?tab=team',
    steps: creatingTeamSteps,
  },
  {
    id: 'inviting-members',
    startRoute: '/business?tab=team',
    steps: invitingMembersSteps,
    requiredRoles: ['owner', 'admin'],
  },
  {
    id: 'comp-database',
    startRoute: '/comp-database',
    steps: compDatabaseSteps,
  },
  {
    id: 'trainee-logging',
    startRoute: '/trainee/experience-log',
    steps: traineeLoggingSteps,
    requiredRoles: ['trainee'],
  },
  {
    id: 'supervisor-review',
    startRoute: '/trainee/experience-log',
    steps: supervisorReviewSteps,
    requiredRoles: ['owner', 'admin', 'appraiser'],
  },
  {
    id: 'billing',
    startRoute: '/settings',
    steps: billingSteps,
  },
  {
    id: 'account-security',
    startRoute: '/settings',
    steps: accountSecuritySteps,
  },
  {
    id: 'building-website',
    startRoute: '/business?tab=website',
    steps: buildingWebsiteSteps,
  },
  {
    id: 'company-profile',
    startRoute: '/business?tab=company',
    steps: companyProfileSteps,
  },
  {
    id: 'market-report',
    startRoute: '/appraisals/tools',
    steps: marketReportSteps,
  },
  {
    id: 'tax-appeal',
    startRoute: '/appraisals/tools',
    steps: taxAppealSteps,
  },
  {
    id: 'sketch-tool',
    startRoute: '/appraisals/tools',
    steps: sketchToolSteps,
  },
  {
    id: 'dcf-analyzer',
    startRoute: '/appraisals/tools',
    steps: dcfAnalyzerSteps,
  },
  {
    id: 'creating-appraisal',
    startRoute: '/appraisals',
    steps: creatingAppraisalSteps,
  },
  {
    id: 'creating-engagement',
    startRoute: '/engagements/log',
    steps: creatingEngagementSteps,
  },
  {
    id: 'managing-engagements',
    startRoute: '/engagements/log',
    steps: managingEngagementsSteps,
  },
  {
    id: 'eo-check',
    startRoute: '/settings',
    steps: eoCheckSteps,
  },
];

export const tourRegistry = new Map<string, TourDefinition>(
  [
    ['Creating Your Account', 'creating-account'],
    ['Setting Up Your Profile', 'setting-up-profile'],
    ['Adding Your License & Getting Verified', 'adding-license'],
    ['Navigating the Dashboard', 'navigating-dashboard'],
    ['Using the Calendar', 'using-calendar'],
    ['Managing Website Inquiries', 'managing-inquiries'],
    ['Converting Leads to Appraisals', 'converting-leads'],
    ['Setting Up Mileage Tracking', 'mileage-setup'],
    ['Generating & Exporting Your Mileage Report', 'mileage-report'],
    ['Creating & Managing a Team', 'creating-team'],
    ['Inviting Members & Assigning Roles', 'inviting-members'],
    ['Using the Comparable Sales Database', 'comp-database'],
    ['Logging Your Experience Hours (Trainee)', 'trainee-logging'],
    ['Reviewing & Approving Entries (Supervisor)', 'supervisor-review'],
    ['Managing Your Subscription & Credits', 'billing'],
    ['Account Security & Preferences', 'account-security'],
    ['Building Your Public Website', 'building-website'],
    ['Managing Your Company Profile', 'company-profile'],
    ['Market Data Report', 'market-report'],
    ['Tax Appeal Feasibility Analysis', 'tax-appeal'],
    ['Floor Plan Sketch Tool', 'sketch-tool'],
    ['DCF Analyzer', 'dcf-analyzer'],
    ['Creating a New Appraisal', 'creating-appraisal'],
    ['Creating an Engagement Letter', 'creating-engagement'],
    ['Managing the Engagement Log', 'managing-engagements'],
    ['Running an E&O Check', 'eo-check'],
  ].map(([title, id]) => [
    title,
    definitions.find((d) => d.id === id)!,
  ])
);
